// src/components/VennLinkShareModal.jsx
import { useState } from 'react';
import { X, Copy, Mail, MessageCircle, Check, Share2 } from 'lucide-react';
import Modal from './Modal';
import styles from './VennLinkShareModal.module.css';

export default function VennLinkShareModal({ vennLink, onClose }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(vennLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Schedule a meeting: ${vennLink.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to schedule a meeting with you. Please use this link to book a time that works for you:\n\n${vennLink.url}\n\n` +
      `Meeting: ${vennLink.title}\n` +
      `Duration: ${vennLink.duration_minutes} minutes\n\n` +
      (vennLink.description ? `${vennLink.description}\n\n` : '') +
      `Looking forward to connecting!\n`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `Hi! I'd like to schedule a meeting with you. Please book a time here: ${vennLink.url}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaSMS = () => {
    const text = encodeURIComponent(`Hi! Please book a meeting with me here: ${vennLink.url}`);
    window.open(`sms:?&body=${text}`);
  };

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(vennLink.url)}`;

  return (
    <Modal isOpen={true} onClose={onClose} title="Share Venn Link" size="md">
      <div className={styles.header}>
        <div>
          <h2 className={styles.headerTitle}>
            <Share2 size={24} className={styles.headerTitleIcon} aria-hidden="true" />
            Share Venn Link
          </h2>
          <p className={styles.headerSubtitle}>{vennLink.title}</p>
        </div>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Close">
          <X size={24} />
        </button>
      </div>

      <div className={styles.body}>
        {/* Booking Link */}
        <div>
          <p className={styles.label}>Booking Link</p>
          <div className={styles.urlRow}>
            <input
              type="text"
              value={vennLink.url}
              readOnly
              className={styles.urlInput}
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={copyToClipboard}
              className={`${styles.copyBtn}${copied ? ` ${styles.copyBtnCopied}` : ''}`}
            >
              {copied ? (
                <><Check size={16} aria-hidden="true" /> Copied!</>
              ) : (
                <><Copy size={16} aria-hidden="true" /> Copy</>
              )}
            </button>
          </div>
        </div>

        {/* Share options */}
        <div>
          <p className={styles.label}>Share via</p>
          <div className={styles.shareGrid}>
            <button onClick={shareViaEmail} className={styles.shareBtn}>
              <Mail size={24} aria-hidden="true" />
              <span className={styles.shareBtnLabel}>Email</span>
            </button>
            <button onClick={shareViaWhatsApp} className={styles.shareBtn}>
              <MessageCircle size={24} aria-hidden="true" />
              <span className={styles.shareBtnLabel}>WhatsApp</span>
            </button>
            <button onClick={shareViaSMS} className={styles.shareBtn}>
              <MessageCircle size={24} aria-hidden="true" />
              <span className={styles.shareBtnLabel}>SMS</span>
            </button>
            <button onClick={copyToClipboard} className={styles.shareBtn}>
              <Copy size={24} aria-hidden="true" />
              <span className={styles.shareBtnLabel}>Copy Link</span>
            </button>
          </div>
        </div>

        {/* QR Code */}
        <div>
          <p className={styles.label}>QR Code</p>
          <div className={styles.qrBox}>
            <img src={qrCodeUrl} alt="QR Code" className={styles.qrImg} width="200" height="200" />
            <p className={styles.qrCaption}>Scan this code to open the booking page</p>
            <a
              href={qrCodeUrl}
              download={`venn-link-${vennLink.short_code}.png`}
              className={styles.qrDownload}
            >
              Download QR Code
            </a>
          </div>
        </div>

        {/* Meeting Details */}
        <div className={styles.detailsBox}>
          <h3 className={styles.detailsTitle}>Meeting Details</h3>
          <div className={styles.detailsList}>
            <p><strong>Duration:</strong> {vennLink.duration_minutes} minutes</p>
            {vennLink.description && (
              <p><strong>Description:</strong> {vennLink.description}</p>
            )}
            <p><strong>Bookings:</strong> {vennLink.bookings_count} so far</p>
            {vennLink.max_bookings && (
              <p><strong>Max bookings:</strong> {vennLink.max_bookings}</p>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className={styles.tipsBox}>
          <h3 className={styles.tipsTitle}>Sharing Tips</h3>
          <ul className={styles.tipsList}>
            <li>Share via email for professional contacts</li>
            <li>Use WhatsApp/SMS for quick scheduling</li>
            <li>Print the QR code for in-person events</li>
            <li>Add the link to your email signature</li>
          </ul>
        </div>
      </div>

      <div className={styles.footer}>
        <button onClick={onClose} className={styles.doneBtn}>Done</button>
      </div>
    </Modal>
  );
}
